import React from 'react'
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    LineChart,
    Line,
    RadialBarChart,
    RadialBar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts'

/* ─── Quarterly Dataset ─── */
const quarterlyData = [
    { quarter: 'Q1', participation: 45, wellnessScore: 52, weeklyWorkouts: 0.8, goalsAchieved: 22 },
    { quarter: 'Q2', participation: 58, wellnessScore: 60, weeklyWorkouts: 1.2, goalsAchieved: 35 },
    { quarter: 'Q3', participation: 64, wellnessScore: 66, weeklyWorkouts: 1.6, goalsAchieved: 44 },
    { quarter: 'Q4', participation: 71, wellnessScore: 71, weeklyWorkouts: 1.9, goalsAchieved: 53 },
]

/* Radial data needs individual entries for RadialBarChart */
const radialData = [
    { name: 'Q4', value: 53, fill: '#f97316' },
    { name: 'Q3', value: 44, fill: '#f59e0b' },
    { name: 'Q2', value: 35, fill: '#60a5fa' },
    { name: 'Q1', value: 22, fill: '#6b7280' },
]

/* ─── Shared dark tooltip style ─── */
const DarkTooltip = ({ active, payload, label, suffix = '' }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-slate-800 border border-white/10 rounded-xl px-4 py-3 shadow-xl text-sm">
                <p className="text-white/60 mb-1 font-semibold">{label}</p>
                {payload.map((entry, i) => (
                    <p key={i} style={{ color: entry.color }} className="font-bold">
                        {entry.name}: {entry.value}{suffix}
                    </p>
                ))}
            </div>
        )
    }
    return null
}

/* ─── Card wrapper ─── */
const ChartCard = ({ title, subtitle, icon, children }) => (
    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-5 md:p-6 border border-white/10 flex flex-col gap-4">
        <div>
            <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">{icon}</span>
                <h3 className="text-base font-bold text-white/90">{title}</h3>
            </div>
            <p className="text-xs text-white/40 pl-7">{subtitle}</p>
        </div>
        <div className="w-full h-52 md:h-64">
            {children}
        </div>
    </div>
)

/* ═══════════════════════════════ MAIN EXPORT ═══════════════════════════════ */
export const CorporateWellnessCharts = () => {
    return (
        <section className="relative bg-slate-900 overflow-hidden">
            {/* Mesh blobs */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-500/8 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 max-w-6xl mx-auto px-4 py-16 md:py-24">
                {/* Section header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                        <span className="text-sm">📈</span>
                        <span className="text-xs font-medium text-white/70">Quarterly Results</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                        Real Results. Measured Every Quarter.
                    </h2>
                    <p className="text-white/50 mt-3 max-w-2xl mx-auto text-sm leading-relaxed">
                        The data below reflects average outcomes observed across companies enrolled in our wellness programs
                        over a 12-month period (Q1–Q4).
                    </p>
                </div>

                {/* 2×2 Chart Grid */}
                <div className="grid md:grid-cols-2 gap-5 md:gap-6">

                    {/* 1. Employee Participation Rate — AreaChart */}
                    <ChartCard
                        icon="👥"
                        title="Employee Participation Rate"
                        subtitle="% of employees actively engaged in the program each quarter"
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={quarterlyData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="participationGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#f97316" stopOpacity={0.35} />
                                        <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                <XAxis dataKey="quarter" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} axisLine={false} tickLine={false} />
                                <YAxis domain={[0, 100]} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
                                <Tooltip content={<DarkTooltip suffix="%" />} />
                                <Area
                                    type="monotone"
                                    dataKey="participation"
                                    name="Participation"
                                    stroke="#f97316"
                                    strokeWidth={2.5}
                                    fill="url(#participationGradient)"
                                    dot={{ r: 5, fill: '#f97316', strokeWidth: 0 }}
                                    activeDot={{ r: 7, fill: '#f97316' }}
                                    isAnimationActive={true}
                                    animationDuration={1000}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    {/* 2. Wellness Score Improvement — BarChart */}
                    <ChartCard
                        icon="🏅"
                        title="Average Employee Wellness Score"
                        subtitle="Composite wellness score out of 100 tracked per quarter"
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={quarterlyData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }} barCategoryGap="35%">
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                <XAxis dataKey="quarter" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} axisLine={false} tickLine={false} />
                                <YAxis domain={[0, 100]} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} axisLine={false} tickLine={false} />
                                <Tooltip content={<DarkTooltip />} />
                                <Bar
                                    dataKey="wellnessScore"
                                    name="Wellness Score"
                                    fill="#f59e0b"
                                    radius={[6, 6, 0, 0]}
                                    isAnimationActive={true}
                                    animationDuration={1000}
                                    shape={(props) => {
                                        const { x, y, width, height, index } = props
                                        const opacity = 0.45 + (index ?? 0) * 0.18
                                        return (
                                            <rect
                                                x={x}
                                                y={y}
                                                width={width}
                                                height={height}
                                                fill={`rgba(245, 158, 11, ${opacity})`}
                                                rx={6}
                                                ry={6}
                                            />
                                        )
                                    }}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    {/* 3. Weekly Workout Sessions — LineChart */}
                    <ChartCard
                        icon="🏋️"
                        title="Avg. Weekly Workout Sessions"
                        subtitle="Average number of workout sessions completed per employee per week"
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={quarterlyData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                <XAxis dataKey="quarter" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} axisLine={false} tickLine={false} />
                                <YAxis domain={[0, 3]} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}x`} />
                                <Tooltip content={<DarkTooltip suffix=" sessions/wk" />} />
                                <Line
                                    type="monotone"
                                    dataKey="weeklyWorkouts"
                                    name="Workouts/Week"
                                    stroke="#10b981"
                                    strokeWidth={2.5}
                                    dot={{ r: 5, fill: '#10b981', strokeWidth: 0 }}
                                    activeDot={{ r: 7 }}
                                    isAnimationActive={true}
                                    animationDuration={1000}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    {/* 4. Fitness Goal Achievement — RadialBarChart */}
                    <ChartCard
                        icon="🎯"
                        title="Fitness Goals Achieved"
                        subtitle="% of employees who met their personal fitness goals per quarter"
                    >
                        <ResponsiveContainer width="100%" height="100%">
                            <RadialBarChart
                                cx="50%"
                                cy="50%"
                                innerRadius="20%"
                                outerRadius="90%"
                                barSize={14}
                                data={radialData}
                                startAngle={90}
                                endAngle={-270}
                            >
                                <RadialBar
                                    minAngle={15}
                                    background={{ fill: 'rgba(255,255,255,0.04)' }}
                                    clockWise
                                    dataKey="value"
                                    isAnimationActive={true}
                                    animationDuration={1000}
                                    label={{ position: 'insideStart', fill: 'rgba(255,255,255,0.55)', fontSize: 11, formatter: v => `${v}%` }}
                                />
                                <Legend
                                    iconSize={10}
                                    layout="vertical"
                                    verticalAlign="middle"
                                    align="right"
                                    formatter={(value) => (
                                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{value}</span>
                                    )}
                                />
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            const d = payload[0].payload
                                            return (
                                                <div className="bg-slate-800 border border-white/10 rounded-xl px-4 py-2 shadow-xl text-sm">
                                                    <p className="font-bold" style={{ color: d.fill }}>{d.name}: {d.value}%</p>
                                                </div>
                                            )
                                        }
                                        return null
                                    }}
                                />
                            </RadialBarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                </div>

                {/* ─── Disclaimer ─── */}
                <div className="mt-8 bg-white/3 border border-white/8 rounded-2xl px-5 py-4 flex gap-3 items-start">
                    <span className="text-amber-400 text-lg shrink-0 mt-0.5">⚠️</span>
                    <div>
                        <p className="text-xs font-semibold text-amber-400/80 uppercase tracking-wider mb-1">Disclaimer</p>
                        <p className="text-xs text-white/40 leading-relaxed">
                            The wellness metrics and improvements shown above are illustrative benchmarks based on average outcomes
                            observed across companies enrolled in our programs. Actual results may vary significantly depending on
                            individual employee commitment, employer participation levels, company culture, program tier selected,
                            and consistency of engagement over time. StringsSG does not guarantee specific outcomes. These charts
                            are intended as indicative projections only and should not be relied upon as contractual commitments.
                        </p>
                    </div>
                </div>

            </div>
        </section>
    )
}
